import React, { FC, ReactNode, useEffect, useState } from "react";
import { useErrorHandler } from "../hooks/useErrorHandler";
import { UserObject } from "../models/User";
import { AuthContextInterface } from "./auth";
import {
  getPartnerById,
  getPartnersByRequest,
  retryPartnerById,
} from "../services/partners";
import { useAuth } from "../hooks/useAuth";
import { useCommonFilters } from "../hooks/useCommonFilters";

export interface PartnersState {
  hideRejectedPartners: boolean;
  [key: string]: any;
}

export const initialState: PartnersState = {
  hideRejectedPartners: true,
};

export interface PartnersStore {
  partnerData?: UserObject;
  partnersData: UserObject[];
  hideRejectedPartners: boolean;
  partnersLoading: boolean;
  partnerLoading: boolean;
  fetchPartnersData: (auth: AuthContextInterface, status: string) => void;
  fetchPartnerDetails: (auth: AuthContextInterface, docId: string) => void;
  retryPartnerCalculation: (auth: AuthContextInterface) => void;
  searchPartners: (search: string) => void;
  toggleRejectedPartners: () => void;
  clearSearchPartners: () => void;
}

const PartnersContext = React.createContext<PartnersStore>({
  partnerData: undefined,
  partnersData: [],
  hideRejectedPartners: initialState.hideRejectedPartners,
  partnersLoading: false,
  partnerLoading: false,
  fetchPartnersData: () => null,
  fetchPartnerDetails: () => null,
  retryPartnerCalculation: () => null,
  searchPartners: () => null,
  toggleRejectedPartners: () => null,
  clearSearchPartners: () => null,
});

interface PartnersContextProviderProps {
  children: ReactNode;
}

export const PartnersContextProvider: FC<PartnersContextProviderProps> = ({
  children,
}) => {
  const { errorHandler } = useErrorHandler();
  const auth = useAuth();
  const { selectedStatus } = useCommonFilters();

  const [partnerData, setPartnerData] = useState<UserObject>();
  const [partnersData, setPartnersData] = useState<Array<UserObject>>([]);
  const [partnersLoading, setPartnersLoading] = useState(false);
  const [partnerLoading, setPartnerLoading] = useState(false);

  const [hideRejectedPartners, setHideRejectedPartners] = useState(
    initialState.hideRejectedPartners
  );
  const toggleRejectedPartners = () =>
    setHideRejectedPartners((hideRejectedPartners) => !hideRejectedPartners);

  const [partnersSearch, setPartnersSearch] = useState("");
  const searchPartners = (search: string) => {
    setPartnersSearch(`search=${search}`);
  };
  const clearSearchPartners = () => setPartnersSearch("");

  const fetchPartnersData = async (
    auth: AuthContextInterface,
    status: string
  ) => {
    const statusQuery =
      status === "all"
        ? ""
        : status === "disabled"
        ? `disabled=true`
        : `status=${status}&disabled=false`;
    try {
      setPartnersLoading(true);
      const { data }: { data: Array<UserObject> } = await getPartnersByRequest(
        { ...auth },
        `role=consultant&${statusQuery}`
      );
      const arrayData: Array<UserObject> = [...data];
      setPartnersData(arrayData);
    } catch (e) {
      errorHandler(new Error("Erro ao buscar parceiros"));
    }
    setPartnersLoading(false);
  };

  const fetchPartnerDetails = async (
    auth: AuthContextInterface,
    docId: string
  ) => {
    setPartnerLoading(true);
    try {
      const { data }: { data: UserObject } = await getPartnerById(
        { ...auth },
        docId
      );
      setPartnerData(data);
    } catch (e) {
      errorHandler(new Error("Erro ao buscar detalhes do parceiro"));
    }
    setPartnerLoading(false);
  };

  const retryPartnerCalculation = async (auth: AuthContextInterface) => {
    if (partnerData?._id) {
      try {
        await retryPartnerById({ ...auth }, partnerData?._id);
      } catch (e) {
        errorHandler(new Error("Erro ao recalcular parceiro"));
      }
      await fetchPartnerDetails({ ...auth }, partnerData?._id);
    }
  };

  useEffect(() => {
    fetchPartnersData(auth, selectedStatus);
  }, [hideRejectedPartners, partnersSearch, selectedStatus]);

  return (
    <PartnersContext.Provider
      value={{
        partnerData,
        partnersData,
        hideRejectedPartners,
        partnersLoading,
        partnerLoading,
        fetchPartnersData,
        fetchPartnerDetails,
        retryPartnerCalculation,
        searchPartners,
        toggleRejectedPartners,
        clearSearchPartners,
      }}
    >
      {children}
    </PartnersContext.Provider>
  );
};

export default PartnersContext;
